import React, { useEffect, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { PROVINCES } from '../lib/taxService';
import { getLicenceProvider } from '../lib/licenceProvider';
import { useT } from '../lib/i18n';
import { toast } from 'sonner';

const STR = {
  fr: {
    close_aria:  'Fermer',
    eyebrow:     'NOUVEAU · TA PROVINCE',
    title:       'Confirme ta province de travail',
    desc_pre:    "On vient d'ajouter le support des entrepreneurs partout au Canada. Confirme ta province pour qu'on adapte les ",
    desc_taxes:  'taxes',
    desc_mid1:   ', les ',
    desc_prices: 'prix',
    desc_mid2:   ' et la ',
    desc_verif:  'vérification de licence',
    desc_post:   ' (RBQ, HCRA, BC Housing, etc.).',
    select_label: 'Sélectionne ta province',
    preview_field_short: 'Numéro de licence',
    preview_field_long:  "Numéro de licence d'entrepreneur",
    preview_field_with:  (s) => `Numéro de licence ${s}`,
    preview_open_registry: (name) => `Ouvrir le registre ${name}`,
    preview_with_registry_pre:  'Ton champ licence affichera ',
    preview_with_registry_mid:  ' et un bouton ',
    preview_with_registry_post: '.',
    preview_no_registry: "Cette province n'a pas de registre public centralisé — un message t'indiquera où vérifier ta licence (municipalité, WCB, etc.).",
    saving:   'Sauvegarde…',
    confirm:  'Confirmer',
    footer:   'Tu peux changer ta province à tout moment dans ton profil.',
    saved:    '✓ Province confirmée',
    error:    'Échec — réessaie',
  },
  en: {
    close_aria:  'Close',
    eyebrow:     'NEW · YOUR PROVINCE',
    title:       'Confirm your working province',
    desc_pre:    "We just added support for contractors across Canada. Confirm your province so we can adapt ",
    desc_taxes:  'taxes',
    desc_mid1:   ', ',
    desc_prices: 'prices',
    desc_mid2:   ' and ',
    desc_verif:  'licence verification',
    desc_post:   ' (RBQ, HCRA, BC Housing, etc.).',
    select_label: 'Select your province',
    preview_field_short: 'Licence',
    preview_field_long:  'Contractor licence number',
    preview_field_with:  (s) => `${s} licence number`,
    preview_open_registry: (name) => `Open the ${name} registry`,
    preview_with_registry_pre:  'Your licence field will display ',
    preview_with_registry_mid:  ' and a button ',
    preview_with_registry_post: '.',
    preview_no_registry: "This province doesn't have a centralized public registry — a message will tell you where to verify your licence (municipality, WCB, etc.).",
    saving:   'Saving…',
    confirm:  'Confirm',
    footer:   'You can change your province at any time in your profile.',
    saved:    '✓ Province confirmed',
    error:    'Failed — please try again',
  },
};

/**
 * Per-user localStorage key. We store the user_id so switching account does
 * not silently skip the question for someone else on the same device.
 */
const STORAGE_PREFIX = 'soumissionpro_province_confirmed_v1:';

function hasConfirmed(userId) {
  if (!userId) return true;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + userId) === '1';
  } catch {
    return true;
  }
}

function markConfirmed(userId) {
  if (!userId) return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + userId, '1');
  } catch {
    /* ignore */
  }
}

/**
 * One-time modal shown on the dashboard for existing users who pre-date the
 * mandatory onboarding province slide. Once confirmed (or dismissed via X),
 * it never reappears for that user on that device.
 */
export function ProvinceConfirmModal() {
  const { user } = useAuth();
  const { profile, saveProfile } = useData();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [open, setOpen] = useState(false);
  const [province, setProvince] = useState('QC');
  const [saving, setSaving] = useState(false);

  // Decide whether to open the modal.
  useEffect(() => {
    if (!user || !profile) return;
    if (hasConfirmed(user.id)) return;

    // 🛡️ Skip modal for users whose profile was created more than 10 minutes ago.
    // If a user is "old" they've almost certainly seen and dismissed the modal
    // before — localStorage was just cleared (Safari iOS, private mode, cache clear, etc.).
    // We don't want to nag returning users every login.
    try {
      const createdAt = profile.created_at ? new Date(profile.created_at).getTime() : null;
      if (createdAt && (Date.now() - createdAt) > 10 * 60 * 1000) {
        markConfirmed(user.id);
        return;
      }
    } catch { /* ignore */ }

    // Initial value mirrors what's currently in their profile.
    setProvince((profile.province || 'QC').toUpperCase());
    // Slight delay so other dashboard UI settles first.
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user, profile]);

  if (!open) return null;

  const lp = getLicenceProvider(province);

  const confirm = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Only persist if the user chose something different than what's already there.
      if ((profile?.province || 'QC').toUpperCase() !== province) {
        await saveProfile({ province });
      }
      markConfirmed(user.id);
      setOpen(false);
      toast.success(T.saved);
    } catch (e) {
      toast.error(e.message || T.error);
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => {
    // Explicit dismissal (X button) → mark as confirmed so we don't nag forever.
    // (Keeps existing province as-is.)
    if (user) markConfirmed(user.id);
    setOpen(false);
  };

  /**
   * Backdrop click → close modal but DON'T mark as confirmed yet.
   * This way, the user gets re-prompted next time they open the app.
   * Prevents accidentally losing the chance to set the right province by
   * tapping outside the card by mistake.
   */
  const softClose = () => {
    setOpen(false);
  };

  return (
    <div
      data-testid="province-confirm-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-4 pb-6 pt-10 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) softClose(); }}
    >
      <div
        className="w-full max-w-md rounded-3xl border-2 p-6 shadow-2xl"
        style={{ backgroundColor: '#FAF7F2', borderColor: '#E5DCC9' }}
      >
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{
              backgroundColor: '#7A1F2B',
              color: '#C19A57',
              boxShadow: '0 20px 40px -20px rgba(122, 31, 43, 0.55)',
            }}
          >
            <MapPin size={26} strokeWidth={2.2} />
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="province-confirm-dismiss"
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
            aria-label={T.close_aria}
          >
            <X size={18} />
          </button>
        </div>

        <div className="text-[10px] font-black tracking-[0.3em] mb-2" style={{ color: '#7A1F2B' }}>
          {T.eyebrow}
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl font-black leading-tight mb-2" style={{ color: '#1F1F1F' }}>
          {T.title}
        </h2>

        <p className="text-sm leading-relaxed mb-5" style={{ color: '#4A5568' }}>
          {T.desc_pre}<b>{T.desc_taxes}</b>{T.desc_mid1}<b>{T.desc_prices}</b>{T.desc_mid2}<b>{T.desc_verif}</b>{T.desc_post}
        </p>

        <label
          className="text-[11px] font-black tracking-[0.2em] uppercase mb-2 block"
          style={{ color: '#6B7280' }}
        >
          {T.select_label}
        </label>
        <select
          data-testid="province-confirm-select"
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className="w-full py-4 px-4 text-base font-bold rounded-2xl border-2 mb-4"
          style={{ borderColor: '#7A1F2B', color: '#1F1F1F', backgroundColor: '#FFFFFF' }}
        >
          {PROVINCES.map((p) => (
            <option key={p.code} value={p.code}>
              {p.code} — {p.name}
            </option>
          ))}
        </select>

        {/* Live preview of what their licence label will become */}
        <div
          className="p-3 rounded-xl border text-xs flex items-start gap-2 mb-5"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5DCC9', color: '#4A5568' }}
        >
          <span style={{ color: '#7A1F2B', fontSize: 14, lineHeight: 1 }}>✓</span>
          <span>
            {lp.hasRegistry ? (
              <>
                {T.preview_with_registry_pre}
                <b>« {lp.short === 'Licence' ? T.preview_field_long : T.preview_field_with(lp.short)} »</b>
                {T.preview_with_registry_mid}
                <b>« {T.preview_open_registry(lp.registryName)} »</b>
                {T.preview_with_registry_post}
              </>
            ) : (
              T.preview_no_registry
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={confirm}
          disabled={saving}
          data-testid="province-confirm-btn"
          className="w-full h-14 rounded-2xl font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
          style={{
            backgroundColor: '#7A1F2B',
            color: '#FFFFFF',
            boxShadow: '0 20px 40px -20px rgba(122, 31, 43, 0.6)',
          }}
        >
          {saving ? T.saving : T.confirm}
        </button>
        <p className="text-center text-[11px] mt-3" style={{ color: '#9CA3AF' }}>
          {T.footer}
        </p>
      </div>
    </div>
  );
}
